import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Tabs as MantineTabs,
  FloatingIndicator,
} from '@mantine/core';

import classes from './Tabs.module.scss';

/**
 * Configuration for a single tab item
 */
interface Tab {
  /** Unique identifier for the tab */
  value: string;
  /** Display label for the tab */
  label: string;
  /** Content to render when tab is active */
  content: ReactNode;
  /** Optional icon/element to show on the left side of label */
  leftSection?: ReactNode;
  /** Optional icon/element to show on the right side of label */
  rightSection?: ReactNode;
  /** Custom styling for the tab */
  style?: CSSProperties;
}

/**
 * Props for the Tabs component
 */
interface TabsProps {
  /** Default tab to show when no URL parameter is present */
  defaultTab: string;
  /** Array of tab configurations */
  tabs: Tab[];
}

/**
 * Tabs - A URL-synchronized tab navigation component for multi-section interfaces.
 * 
 * This component provides tabbed navigation with:
 * - URL parameter synchronization (bookmarkable tab states)
 * - Smooth animated tab indicator
 * - Responsive design for mobile and desktop
 * - Support for icons and badges on tab labels
 * - Automatic state management via URL search params
 * 
 * Key Features:
 * - Tab state persisted in URL as 'tab' search parameter
 * - Animated floating indicator for visual feedback
 * - Custom styling support for individual tabs
 * - Responsive behavior with proper mobile handling
 * - Integration with React Router for navigation state
 * 
 * Used extensively in:
 * - Reception settings (Training, Staff Directory, Settings tabs)
 * - User profile management (Profile, General Settings)
 * - Reporting interfaces for different data views
 * 
 * @example
 * const tabs = [
 *   { value: 'overview', label: 'Overview', content: <OverviewPage /> },
 *   { value: 'settings', label: 'Settings', content: <SettingsPage />, rightSection: <Badge>2</Badge> }
 * ];
 * 
 * <Tabs defaultTab="overview" tabs={tabs} />
 * 
 * @param props - Tabs component props
 * @returns JSX.Element
 */
const Tabs = ({ defaultTab, tabs }: TabsProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || defaultTab;

  const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
  const [value, setValue] = useState<string | null>('1');
  const [controlsRefs, setControlsRefs] = useState<
    Record<string, HTMLButtonElement | null>
  >({});
  const setControlRef = (val: string) => (node: HTMLButtonElement) => {
    controlsRefs[val] = node;
    setControlsRefs(controlsRefs);
  };

  const handleTabChange = (value: string | null) => {
    if (value) {
      setSearchParams({ tab: value });
    }
    setValue(value);
  };

  useEffect(() => {
    setValue(activeTab);
  }, [activeTab]);

  return (
    <MantineTabs value={activeTab} onChange={handleTabChange} variant="none">
      <MantineTabs.List
        ref={setRootRef}
        className={classes.list}
        mb={{ base: 20, md: 28 }}
      >
        {tabs.map((tab) => (
          <MantineTabs.Tab
            key={tab.value}
            value={tab.value}
            className={classes.tab}
            leftSection={tab.leftSection}
            ref={setControlRef(tab.value)}
            rightSection={tab.rightSection}
            style={tab.style}
          >
            {tab.label}
          </MantineTabs.Tab>
        ))}
        <FloatingIndicator
          parent={rootRef}
          className={classes.indicator}
          style={{ background: 'inherit' }}
          target={value ? controlsRefs[value] : null}
        />
      </MantineTabs.List>

      <MantineTabs.Panel
        key={activeTab}
        value={activeTab}
        className={classes.panel}
      >
        {tabs.find((tab) => tab.value === activeTab)?.content}
      </MantineTabs.Panel>
    </MantineTabs>
  );
};

export default Tabs;
